// src/core/print/printContext.js
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchActiveCompany } from "../redux/company.slice";
import { nunitoRegularBase64 } from "../../style/fonts/nunito-regular-base64";
import { nunitoBoldBase64 } from "../../style/fonts/nunito-bold-base64";
import { fetchBankAccounts } from "../redux/bankAccount.slice";
/* ------------------ IMAGE → BASE64 ------------------ */
const toBase64 = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            } catch (err) {
                reject(err);
            }
        };

        img.onerror = reject;
        img.src = url;
    });
const cssColorToRgb = (variable) => {
    const color = getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();

    if (!color) return [0, 0, 0];

    // HEX → RGB
    if (color.startsWith("#")) {
        const hex = color.replace("#", "");

        return [
            parseInt(hex.substring(0, 2), 16),
            parseInt(hex.substring(2, 4), 16),
            parseInt(hex.substring(4, 6), 16),
        ];
    }

    return [0, 0, 0];
};
/* ------------------ MAIN HOOK ------------------ */
export function usePrintContext() {
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { bankAccounts } = useSelector((state) => state.bankAccounts);
    const [logoBase64, setLogoBase64] = useState(null);
    const attempted = useRef(false);

    /* -------- Fetch company once -------- */
    useEffect(() => {
        if (!company) dispatch(fetchActiveCompany());
    }, [company, dispatch]);

    /* -------- Resolve logo URL -------- */
    const logoUrl = useMemo(() => {
        if (!company?.photos?.length) return null;

        return (
            company.photos.find((p) => p?.isPrincipal && p?.presignedUrl)?.presignedUrl ||
            company.photos.find((p) => p?.presignedUrl)?.presignedUrl ||
            null
        );
    }, [company]);

    /* -------- Convert logo once -------- */
    useEffect(() => {
        if (!logoUrl || attempted.current) return;
        attempted.current = true;

        toBase64(logoUrl)
            .then(setLogoBase64)
            .catch((e) => console.warn("Logo conversion failed", e));
    }, [logoUrl]);
    /*-------------------------------------*/
    const pdfColors = useMemo(() => ({
        primary: cssColorToRgb("--primary"),
        secondary: cssColorToRgb("--secondary"),
        white: [255, 255, 255],
        lightGray: [245, 245, 245],
        text: [40, 40, 40],
    }), []);


    const getPdfColor = useCallback((type = "primary") => {
        return pdfColors[type] || pdfColors.primary;
    }, [pdfColors]);
    /* -------- Build société -------- */
    const societeDetails = useMemo(() => {
        return [
            company?.companyName || "",
            [company?.companyAddress, company?.companyTown].filter(Boolean).join(", "),
            company?.companyEmail ? `Email : ${company.companyEmail}` : "",
            company?.companyPhone ? `Tél : (+216) ${company.companyPhone}` : "",
        ].filter(Boolean);
    }, [company]);

    /* ------------------ PDF HELPERS ------------------ */

    const registerFonts = (doc) => {
        doc.addFileToVFS("Nunito-Regular.ttf", nunitoRegularBase64);
        doc.addFont("Nunito-Regular.ttf", "Nunito", "normal");

        doc.addFileToVFS("Nunito-Bold.ttf", nunitoBoldBase64);
        doc.addFont("Nunito-Bold.ttf", "Nunito", "bold");
    };

    const drawHeader = (doc, options) => {
        const {
            title,
            subtitle,
            period,
            margin,
            pageWidth,
            headerBottomY = 55,
            coordStartY = 34,
        } = options;

        if (logoBase64) {
            try {
                doc.addImage(logoBase64, "PNG", margin, 10, 40, 18);
            } catch (e) {
                console.warn("Image error", e);
            }
        }

        doc.setFont("Nunito", "normal");
        doc.setFontSize(9);
        doc.setTextColor(40, 40, 40);

        societeDetails.forEach((line, i) => {
            doc.text(line, margin, coordStartY + i * 5);
        });

        const rightX = pageWidth - margin;
        let rightY = coordStartY;

        if (title) {
            doc.setFont("Nunito", "bold");
            doc.setFontSize(14);
            doc.text(title, rightX, rightY, { align: "right" });
        }

        doc.setFont("Nunito", "normal");
        doc.setFontSize(10);
        rightY += 6;

        if (subtitle) {
            doc.text(subtitle, rightX, rightY, { align: "right" });
            rightY += 5;
        }

        if (period) {
            doc.text(period, rightX, rightY, { align: "right" });
        }

        doc.line(margin, headerBottomY, pageWidth - margin, headerBottomY);
    };

    /* -------- Fetch bank accounts once -------- */
    const hasFetchedBankAccounts = useRef(false);

    useEffect(() => {
        if (!hasFetchedBankAccounts.current) {
            hasFetchedBankAccounts.current = true;
            dispatch(fetchBankAccounts());
        }
    }, [dispatch]);


    /* -------- Active bank account -------- */
    const activeBankAccount = useMemo(() => {
        return (
            bankAccounts?.find(
                (account) =>
                    account.bank_account_is_active === true ||
                    account.isActive === true ||
                    account.active === true
            ) || bankAccounts?.[0] || null
        );
    }, [bankAccounts]);

    /* -------- Bank details -------- */
    const bankDetails = useMemo(() => ({
        bankName: activeBankAccount?.bank_name || "",
        bic: activeBankAccount?.bic || "",
        rib: activeBankAccount?.rib || "",
    }), [activeBankAccount]);

    const drawFooter = (doc, { pageNumber, margin, pageWidth, pageHeight }) => {
        doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);

        const year = new Date().getFullYear();

        doc.setFont("Nunito", "normal");
        doc.setFontSize(9);

        doc.text(
            `${company?.companyName || "App"} © ${year}`,
            margin,
            pageHeight - 10
        );

        doc.text(
            `Page ${pageNumber} / {total_pages_count_string}`,
            pageWidth - margin,
            pageHeight - 10,
            { align: "right" }
        );
    };

    return {
        company,
        logoBase64,
        societeDetails,
        bankDetails,
        registerFonts,
        drawHeader,
        drawFooter,
        getPdfColor
    };
}